import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { BarChart3, TrendingDown, TrendingUp } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Cell, XAxis, YAxis } from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { RevenueChartCard } from "../revenue/RevenueChartCard";
import { RevenueChartLegend } from "../revenue/RevenueChartLegend";
import {
  SERVICES_CHART_CARD_ICON_COLOR,
  SERVICES_WEEKLY_USAGE_BAR_COLOR,
  SERVICES_WEEKLY_USAGE_BAR_HIGHLIGHT_COLOR,
} from "./constants";
import type { DailyRevenuePoint } from "./serviceDetailTypes";
import { formatAnalyticsCurrency } from "../shared/analyticsFormatting";

interface ServiceWeeklyUsageChartProps {
  data: DailyRevenuePoint[];
  trendLabel: string;
  trendSubtext: string;
  highlightDay?: string;
  onExport?: (format: "csv" | "xlsx") => void;
  exportDisabled?: boolean;
}

function isTrendingDown(label: string): boolean {
  return /\btrending\s+down\b/i.test(label);
}

export const ServiceWeeklyUsageChart: React.FC<
  ServiceWeeklyUsageChartProps
> = ({
  data,
  trendLabel,
  trendSubtext,
  highlightDay,
  onExport,
  exportDisabled,
}) => {
  const { t } = useTranslation();
  const revenueLabel = t("analytics.overview.revenueSeries", "Revenue");
  const chartConfig = useMemo(
    () =>
      ({
        revenue: {
          label: revenueLabel,
          color: SERVICES_WEEKLY_USAGE_BAR_COLOR,
        },
      }) satisfies ChartConfig,
    [revenueLabel],
  );
  const legendItems = useMemo(
    () => [
      {
        key: "revenue",
        label: revenueLabel,
        color: SERVICES_WEEKLY_USAGE_BAR_COLOR,
      },
    ],
    [revenueLabel],
  );
  const trendingDown = isTrendingDown(trendLabel);
  const TrendIcon = trendingDown ? TrendingDown : TrendingUp;

  return (
    <RevenueChartCard
      title={t("analytics.services.detail.dailyRevenue", "Revenue by day")}
      icon={<BarChart3 />}
      iconColor={SERVICES_CHART_CARD_ICON_COLOR}
      onExport={onExport}
      exportDisabled={exportDisabled}
    >
      <ChartContainer
        config={chartConfig}
        className="!aspect-auto h-[280px] w-full"
      >
        <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid vertical={false} strokeDasharray="3 3" />
          <XAxis
            dataKey="day"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            className="text-muted-foreground text-xs"
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            width={32}
            className="text-muted-foreground text-xs"
          />
          <ChartTooltip
            cursor={false}
            content={
              <ChartTooltipContent
                indicator="line"
                formatter={(value) =>
                  formatAnalyticsCurrency(Number(value), {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                    symbol: "€",
                    symbolPosition: "suffix",
                  })
                }
              />
            }
          />
          <Bar dataKey="revenue" radius={[6, 6, 0, 0]}>
            {data.map((entry) => {
              const isHighlighted =
                highlightDay != null && entry.day === highlightDay;

              return (
                <Cell
                  key={entry.day}
                  fill={
                    isHighlighted
                      ? SERVICES_WEEKLY_USAGE_BAR_HIGHLIGHT_COLOR
                      : SERVICES_WEEKLY_USAGE_BAR_COLOR
                  }
                  stroke={
                    isHighlighted
                      ? SERVICES_WEEKLY_USAGE_BAR_HIGHLIGHT_COLOR
                      : undefined
                  }
                  strokeWidth={isHighlighted ? 2 : 0}
                />
              );
            })}
          </Bar>
        </BarChart>
      </ChartContainer>
      <RevenueChartLegend items={legendItems} />
      <div className="space-y-0.5 font-sans">
        <div
          className={
            trendingDown
              ? "flex items-center gap-1.5 text-sm leading-none font-medium text-red-600"
              : "flex items-center gap-1.5 text-sm leading-none font-medium text-green-600"
          }
        >
          <span>{trendLabel}</span>
          <TrendIcon
            className="size-3.5 shrink-0"
            strokeWidth={2}
            aria-hidden
          />
        </div>
        <p className="text-xs leading-normal text-[#737373]">{trendSubtext}</p>
      </div>
    </RevenueChartCard>
  );
};
